import { AccessEnum } from "~/api/GeneratedApi";

export enum LayerTitle {
  OSM = "osm",
  S57 = "s57",
  ShoreMap = "shore-map",
  SeaMap = "sea-map",
  Ports = "ports",
  PortsDraw = "portsDraw",
  NavigationLines = "navigationLines",
  LeadingLines = "leadingLines",
  Fairways = "fairways",
  Vessels = "vessels",
  Wind = "wind",
  IceLevel = "iceLevel",
  SeaLevel = "seaLevel",
  WaveHeight = "waveHeight",
  Conditions = "conditions",
  Events = "events",
  NauticalWarnings = "nautical-warnings",
  EventsDraw = "eventsDraw",
  FleetAIS = "fleet-ais",
  // FleetPTOPCars = "fleet-ptop-cars",
  // FleetPTOPTrailers = "fleet-ptop-trailers",
  Measurements = "measurements",
  Dams = "dams",
  DamsDraw = "damsDraw",
  Ferries = "ferries",
  FerriesDraw = "ferriesDraw",
  Berths = "berths",
  AtoNs = "atons",
  BerthsDraw = "berthsDraw",
  PortServices = "port-services",
  ArrivalZones = "arrival-zones",
  PortServicesDraw = "port-services-draw",
  YepzonTrackerDevices = "yepzon-tracker-devices",
  TaivalTrackerDevices = "taival-tracker-devices",
  // PTOPCars = "ptop-cars",
  // PTOPTrailers = "ptop-trailers",
  Projects = "projects",
  ProjectsPlanning = "projects-planning",
  ProjectPoiItems = "project-poi-items",
  VehicleEquipments = "vehicle-equipments",
}
export enum LayerGroupTitle {
  BackgroundMap = "backgroundMap",
  Events = "eventsAndWarnings",
  Ports = "ports",
  Waterways = "waterways",
  VesselsAndEquipment = "vessels-and-equipment",
  Fleets = "fleets",
  WeatherAndConditions = "weatherAndConditions",
  Measurements = "measurements",
  DamsAndFerries = "damsAndFerries",
  AtoNs = "atons",
  TrackerDevices = "tracker-devices",
  Utils = "utils",
}

type LayerSettings = {
  zIndex: number;
  visible: boolean;
  maxZoom?: number;
  minZoom?: number;
  requiredAccess?: AccessEnum;
  hideSettings?: boolean;
};

export const LayerDefaults: Record<LayerTitle, LayerSettings> = {
  [LayerTitle.OSM]: { zIndex: 100, visible: true },
  [LayerTitle.S57]: { zIndex: 101, visible: false },
  [LayerTitle.SeaMap]: { zIndex: 102, visible: false },
  [LayerTitle.ShoreMap]: { zIndex: 103, visible: false },
  [LayerTitle.NavigationLines]: { zIndex: 200, visible: false },
  [LayerTitle.LeadingLines]: { zIndex: 201, visible: false },
  [LayerTitle.Fairways]: { zIndex: 202, visible: true },
  [LayerTitle.Ports]: {
    zIndex: 300,
    visible: true,
    requiredAccess: AccessEnum.PortRead,
  },
  [LayerTitle.PortsDraw]: { zIndex: 301, visible: true },
  [LayerTitle.Events]: {
    zIndex: 302,
    visible: true,
    requiredAccess: AccessEnum.EventRead,
  },
  [LayerTitle.EventsDraw]: { zIndex: 303, visible: true },
  [LayerTitle.NauticalWarnings]: {
    zIndex: 304,
    visible: true,
    requiredAccess: AccessEnum.EventRead,
  },
  [LayerTitle.Berths]: {
    zIndex: 304,
    visible: true,
    requiredAccess: AccessEnum.PortRead,
  },
  [LayerTitle.BerthsDraw]: { zIndex: 305, visible: true },
  [LayerTitle.ArrivalZones]: {
    zIndex: 306,
    visible: true,
    requiredAccess: AccessEnum.PortRead,
  },
  [LayerTitle.Vessels]: { zIndex: 400, visible: true, minZoom: 5 },
  [LayerTitle.VehicleEquipments]: {
    zIndex: 401,
    visible: true,
    minZoom: 5,
    requiredAccess: AccessEnum.EquipmentManagementRead,
  },
  // [LayerTitle.PTOPTrailers]: {
  //   zIndex: 402,
  //   visible: true,
  //   minZoom: 5,
  //   requiredAccess: AccessEnum.EquipmentManagementRead,
  // },
  [LayerTitle.Wind]: { zIndex: 150, visible: true, minZoom: 5 },
  [LayerTitle.SeaLevel]: { zIndex: 151, visible: true, minZoom: 5 },
  [LayerTitle.WaveHeight]: { zIndex: 152, visible: true, minZoom: 5 },
  [LayerTitle.Conditions]: { zIndex: 153, visible: true },
  [LayerTitle.IceLevel]: { zIndex: 154, visible: true, minZoom: 5 },
  [LayerTitle.FleetAIS]: {
    zIndex: 600,
    visible: true,
    minZoom: 5,
    requiredAccess: AccessEnum.FleetRead,
  },
  // [LayerTitle.FleetPTOPCars]: {
  //   zIndex: 601,
  //   visible: true,
  //   minZoom: 5,
  //   requiredAccess: AccessEnum.EquipmentManagementRead,
  // },
  // [LayerTitle.FleetPTOPTrailers]: {
  //   zIndex: 602,
  //   visible: true,
  //   minZoom: 5,
  //   requiredAccess: AccessEnum.EquipmentManagementRead,
  // },
  [LayerTitle.Measurements]: {
    zIndex: 700,
    visible: true,
    requiredAccess: AccessEnum.SeaSurveyRead,
  },
  [LayerTitle.ProjectsPlanning]: {
    zIndex: 701,
    visible: false,
    requiredAccess: AccessEnum.ProjectRead,
    hideSettings: true,
  },
  [LayerTitle.Projects]: {
    zIndex: 702,
    visible: false,
    requiredAccess: AccessEnum.ProjectRead,
    hideSettings: true,
  },
  [LayerTitle.ProjectPoiItems]: {
    zIndex: 703,
    visible: false,
    requiredAccess: AccessEnum.ProjectRead,
    hideSettings: true,
  },
  [LayerTitle.Dams]: {
    zIndex: 800,
    visible: true,
    requiredAccess: AccessEnum.DamRead,
  },
  [LayerTitle.DamsDraw]: { zIndex: 801, visible: true },
  [LayerTitle.Ferries]: {
    zIndex: 802,
    visible: true,
    requiredAccess: AccessEnum.FerryRead,
  },
  [LayerTitle.FerriesDraw]: { zIndex: 803, visible: true },

  [LayerTitle.AtoNs]: {
    zIndex: 900,
    visible: true,
    requiredAccess: AccessEnum.SafetyDeviceRead,
  },
  [LayerTitle.PortServices]: {
    zIndex: 1000,
    visible: true,
    requiredAccess: AccessEnum.PortRead,
  },
  [LayerTitle.PortServicesDraw]: { zIndex: 1001, visible: true },
  [LayerTitle.YepzonTrackerDevices]: { zIndex: 1100, visible: true },
  [LayerTitle.TaivalTrackerDevices]: { zIndex: 1101, visible: true },
};
